import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff, Shield, UserPlus, UserCheck } from "lucide-react";
import api, { setSession } from "@/lib/api";

export default function LockScreen() {
  const [divisions, setDivisions] = useState([]);
  const [selected, setSelected] = useState("");  // "" | "admin" | division slug
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/auth/divisions")
      .then((r) => setDivisions(r.data.items || []))
      .catch(() => setDivisions([]));
  }, []);

  const options = [
    { slug: "", name: "Select a division…" },
    ...[...divisions].sort((a, b) => a.name.localeCompare(b.name)),
    { slug: "admin", name: "Admin / Leadership" },
  ];

  const submit = async (e) => {
    if (e) e.preventDefault();
    if (!password || !selected) return;
    setLoading(true);
    setError("");
    try {
      if (selected === "admin") {
        const res = await api.post("/auth/admin-login", { passcode: password });
        setSession(res.data.token, res.data.level);
        navigate("/dashboard");
      } else {
        const res = await api.post("/auth/division-login", { division: selected, passcode: password });
        setSession(res.data.token, res.data.level);
        sessionStorage.setItem("rrdc_division", res.data.division);
        sessionStorage.setItem("rrdc_division_name", res.data.division_name);
        // Send division logins to the SAME dashboard admins see. The dashboard
        // backend (`/api/crew-dashboard/today`) is already division-scoped via
        // `_token_division`, so the supervisor sees their division's
        // summary cards (Present / Late / Absent / etc.), attendance table,
        // and the Planned Absences button — same as admin. Previously this
        // navigated straight to /staff-cards, which hid every dashboard
        // feature from divisional supervisors.
        navigate("/dashboard");
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Login failed";
      setError(typeof msg === "string" ? msg : "Login failed");
      setPassword("");
    } finally { setLoading(false); }
  };

  const selectedName = options.find((o) => o.slug === selected)?.name || "";

  return (
    <div className="min-h-screen lock-bg flex items-center justify-center p-4 sm:p-8" data-testid="lock-screen">
      <div className="w-full max-w-md">

        <div className="glass rounded-3xl p-6 sm:p-8 space-y-6">
          {/* Brand */}
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Rolling River Day Camp" className="w-14 h-14 rounded-2xl shadow-md object-contain bg-white p-1" />
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Rolling River Day Camp</div>
              <h1 className="font-display text-xl sm:text-2xl font-bold text-slate-900">Staff Snapshot</h1>
            </div>
          </div>

          {/* Primary: Staff Check-In */}
          <button
            type="button"
            onClick={() => navigate("/staff-checkin")}
            className="w-full btn-touch rounded-2xl bg-[#3556A5] hover:bg-[#2c4889] text-white font-bold text-lg px-5 py-5 inline-flex items-center justify-center gap-3 shadow-lg shadow-[#3556A5]/30"
            data-testid="staff-checkin-btn"
          >
            <UserCheck className="w-5 h-5" /> Staff Check-In
          </button>

          {/* Secondary: Visitor Sign-In */}
          <button
            type="button"
            onClick={() => navigate("/visitor")}
            className="w-full btn-touch rounded-2xl bg-yellow-400 hover:bg-yellow-500 text-slate-900 font-bold text-lg px-5 py-5 inline-flex items-center justify-center gap-3 shadow-lg shadow-yellow-400/40 border-2 border-yellow-300"
            data-testid="visitor-signin-btn"
          >
            <UserPlus className="w-5 h-5" /> Visitor Sign-In
          </button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-slate-200/70" /></div>
            <div className="relative flex justify-center text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
              <span className="px-3 bg-white/80 rounded">Division Sign-In</span>
            </div>
          </div>

          {/* Division picker + passcode */}
          <form onSubmit={submit} className="space-y-3" data-testid="division-login-form">
            <label className="text-xs uppercase tracking-[0.16em] text-slate-500 font-bold block">Division</label>
            <select
              value={selected}
              onChange={(e) => { setSelected(e.target.value); setPassword(""); setError(""); }}
              className="w-full text-base px-4 py-3 rounded-xl bg-white border border-slate-200 outline-none focus:ring-2 focus:ring-blue-400"
              data-testid="division-select"
            >
              {options.map((o) => (
                <option key={o.slug || "placeholder"} value={o.slug} disabled={!o.slug}>{o.name}</option>
              ))}
            </select>

            {selected && (
              <>
                <label className="text-xs uppercase tracking-[0.16em] text-slate-500 font-bold block pt-1">Password</label>
                <div className="relative">
                  <input
                    type={show ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={`${selectedName} password`}
                    autoComplete="current-password"
                    className="w-full text-lg px-4 py-3 rounded-xl bg-white/95 border border-slate-200 outline-none focus:ring-2 focus:ring-blue-400 pr-11"
                    data-testid="passcode-input"
                  />
                  <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-slate-500 hover:text-slate-800" data-testid="toggle-show-passcode">
                    {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>

                {error && <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-sm" data-testid="login-error">{error}</div>}

                <button
                  type="submit"
                  disabled={loading || !password}
                  className={`w-full btn-touch rounded-xl text-white font-bold text-base transition disabled:opacity-50 px-4 py-3 inline-flex items-center justify-center gap-2 ${
                    selected === "admin" ? "bg-violet-600 hover:bg-violet-700" : "bg-blue-600 hover:bg-blue-700"
                  }`}
                  data-testid="submit-passcode"
                >
                  {selected === "admin" && <Shield className="w-4 h-4" />}
                  {loading ? "Signing in…" : "Enter →"}
                </button>
              </>
            )}
          </form>

          <div className="flex items-center justify-between pt-1">
            <span className="text-xs text-slate-400">v2.1</span>
            <span className="text-xs text-slate-400">Supervisors only</span>
          </div>
        </div>

      </div>
    </div>
  );
}
